<script lang="ts" setup>
export interface PostListProps {
    posts: BlogPost[];
    initialCount?: number;
    collapsable?: boolean;
}

const props = withDefaults(defineProps<PostListProps>(), {
    initialCount: 5,
    collapsable: false,
})

const showAll = ref<boolean>(props.initialCount >= props.posts.length)
const postListRef = ref<HTMLUListElement>()

const formattedData = computed<BlogPost[]>((): BlogPost[] => {
    if (!props.collapsable) return props.posts

    return showAll.value
        ? props.posts
        : props.posts.slice(0, props.initialCount)
})

// Focus management for WCAG 2.4.3 - move focus to first new item after expanding
const handleClick = () => {
    const firstNewIndex = props.initialCount
    showAll.value = true

    nextTick(() => {
        // Focus the first newly revealed item
        if (postListRef.value) {
            const listItems = postListRef.value.querySelectorAll<HTMLLIElement>('[data-post-item]')
            const firstNewItem = listItems[firstNewIndex]
            if (firstNewItem) {
                firstNewItem.focus()
            }
        }
    })
}

const getLink = (item: BlogPost): VNuxtLinkTo => {
    if (item.code === 'post') {
        return {
            name: 'blog-detail',
            params: {
                category: item.category.slug,
                id: item.slug,
            },
        }
    }

    return item.slug
}

</script>
<template>
  <ul
    ref="postListRef"
    class="m-0"
  >
    <li
      v-for="(post, postIndex) in formattedData"
      :key="`post-${postIndex}`"
      data-post-item
      class="row position-relative"
      tabindex="-1"
      :class="{
        'border-bottom pb-4 mb-4': postIndex < props.posts.length - 1,
      }"
    >
      <time
        v-if="post.published_at"
        :datetime="post.published_at"
        class="col-12 col-lg-3 d-block flex-shrink-0 mb-1 mb-md-2 mb-lg-0"
      >
        {{ $date(post.published_at) }}
      </time>
      <VNuxtLink
        :to="getLink(post)"
        :label="post.title"
        class="col-12 col-lg-9 text-black text-hover-secondary fw-bold stretched-link"
      >
        {{ post.title }}
      </VNuxtLink>
    </li>
  </ul>

  <VButton
    v-if="props.collapsable && !showAll"
    color="link"
    type="button"
    class="d-flex align-items-center text-secondary"
    @click="handleClick"
  >
    {{ $t("Zobraziť ďalšie správy") }}
    <svg
      class="d-block ms-1 text-secondary"
      aria-hidden="true"
      width="8"
      height="8"
    >
      <use
        :xlink:href="`#${
          props.collapsable ? 'angle-down' : 'angle-right'
        }`"
      />
    </svg>
  </VButton>
</template>
